import * as React from "react"
import { Box, Button, Divider, Popover } from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"

interface TimePickerProps {
  value?: string // HH:mm format
  onChange?: (time: string) => void
  className?: string
  disabled?: boolean
}

/**
 * 24h time picker — a button showing the current `HH:mm` that opens a
 * popover with two scrollable hour/minute columns. Rebuilt on MUI
 * (`Button` + `Popover`, was Radix Popover + a cmdk-style list); keeps the
 * `{ value, onChange }` contract so the CRM task/activity forms are unchanged.
 */
export function TimePicker({
  value = "09:00",
  onChange,
  className,
  disabled = false,
}: TimePickerProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const [hours, minutes] = value.split(":")

  const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"))
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"))

  const handleHourChange = (hour: string) => {
    onChange?.(`${hour}:${minutes}`)
  }

  const handleMinuteChange = (minute: string) => {
    onChange?.(`${hours}:${minute}`)
  }

  const column = (
    options: string[],
    current: string | undefined,
    onPick: (v: string) => void,
  ) => (
    <Box sx={{ height: 200, overflowY: "auto", display: "flex", flexDirection: "column" }}>
      {options.map((opt) => (
        <Button
          key={opt}
          variant={current === opt ? "contained" : "text"}
          color={current === opt ? "primary" : "inherit"}
          onClick={() => onPick(opt)}
          sx={{ justifyContent: "center", px: 2, borderRadius: 0, minWidth: 0 }}
        >
          {opt}
        </Button>
      ))}
    </Box>
  )

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        disabled={disabled}
        {...(className !== undefined ? { className } : {})}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          fontWeight: 400,
          borderColor: "divider",
          color: "text.primary",
        }}
      >
        {value}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
      >
        <Box sx={{ display: "flex" }}>
          {column(hourOptions, hours, handleHourChange)}
          <Divider orientation="vertical" flexItem />
          {column(minuteOptions, minutes, handleMinuteChange)}
        </Box>
      </Popover>
    </>
  )
}
